import React, { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Eye,
  Edit,
  RefreshCw,
  Check,
  X,
  Users,
  Cloud,
  CloudOff,
  Settings,
  ExternalLink,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { outlookCalendarService } from '../../lib/outlookCalendarService';

export default function ContentCalendarEnhanced() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedClient, setSelectedClient] = useState('f8a6a078-1684-4fc0-a7cb-70699d62105b');
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<any>(null);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncedEvents, setSyncedEvents] = useState<any[]>([]);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);

  const clients = [
    { id: 'f8a6a078-1684-4fc0-a7cb-70699d62105b', name: 'TechCorp' },
    { id: 'be540454-84ee-4c39-9d0f-d14039b03913', name: 'Fashion Brand' }
  ];

  const mockPosts = [
    {
      id: '1',
      title: 'Product Launch Post',
      client: 'TechCorp',
      time: '10:00 AM',
      platforms: ['instagram', 'facebook'],
      status: 'scheduled',
      post_type: 'carousel',
      synced: true
    },
    {
      id: '2',
      title: 'Behind the Scenes',
      client: 'Fashion Brand',
      time: '2:00 PM',
      platforms: ['instagram', 'tiktok'],
      status: 'scheduled',
      post_type: 'video',
      synced: false
    }
  ];

  useEffect(() => {
    loadData();
  }, [selectedClient]);

  const loadData = async () => {
    setLoading(true);

    const userId = 'current-user-id';
    const [connResult, collabResult, statusResult, eventsResult] = await Promise.all([
      outlookCalendarService.getConnections(userId),
      outlookCalendarService.getCollaborators(selectedClient),
      outlookCalendarService.getSyncStatus(selectedClient),
      outlookCalendarService.getSyncedEvents(selectedClient)
    ]);

    if (connResult.success && connResult.data.length > 0) {
      setConnections(connResult.data);
      setSelectedConnection(connResult.data[0]);
      setOutlookConnected(connResult.data[0].is_active);
    }

    if (collabResult.success) setCollaborators(collabResult.data);
    if (statusResult.success) setSyncStatus(statusResult.data);
    if (eventsResult.success) setSyncedEvents(eventsResult.data);

    setLoading(false);
  };

  const handleConnectOutlook = async () => {
    const redirectUri = `${window.location.origin}/outlook/callback`;
    const authUrl = await outlookCalendarService.initiateOAuthFlow(redirectUri);
    window.location.href = authUrl;
  };

  const handleDisconnectOutlook = async () => {
    if (!selectedConnection || !confirm('Disconnect from Outlook? This will stop automatic syncing.')) return;

    const result = await outlookCalendarService.disconnectOutlook(selectedConnection.id);
    if (result.success) {
      setOutlookConnected(false);
      alert('Disconnected from Outlook successfully');
      loadData();
    }
  };

  const handleSyncPost = async (postId: string) => {
    if (!selectedConnection) {
      alert('Please connect to Outlook first');
      return;
    }

    setSyncing(true);
    const result = await outlookCalendarService.syncPostToOutlook(postId, selectedConnection.id);

    if (result.success) {
      alert('Post synced to Outlook successfully!');
      loadData();
    } else {
      alert(`Sync failed: ${result.error}`);
    }

    setSyncing(false);
  };

  const handleBulkSync = async () => {
    if (!selectedConnection) {
      alert('Please connect to Outlook first');
      return;
    }

    if (!confirm('Sync all scheduled posts for the next 30 days to Outlook?')) return;

    setSyncing(true);
    const dateFrom = new Date().toISOString();
    const dateTo = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    const result = await outlookCalendarService.bulkSyncToOutlook(
      selectedClient,
      selectedConnection.id,
      dateFrom,
      dateTo
    );

    if (result.success) {
      const { total_posts, synced_count, failed_count } = result.data;
      alert(`Bulk sync complete!\nTotal: ${total_posts}\nSynced: ${synced_count}\nFailed: ${failed_count}`);
      loadData();
    } else {
      alert(`Bulk sync failed: ${result.error}`);
    }

    setSyncing(false);
    setShowSyncModal(false);
  };

  const handleAddCollaborator = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    const collaborator = {
      client_id: selectedClient,
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      role: formData.get('role') as any,
      outlook_email: formData.get('outlook_email') as string,
      receive_calendar_invites: formData.get('receive_invites') === 'on'
    };

    const result = await outlookCalendarService.addCollaborator(collaborator);
    if (result.success) {
      alert('Collaborator added successfully!');
      loadData();
      setShowCollaboratorsModal(false);
    }
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const today = new Date();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === today.toDateString();
      const posts = date.getDate() % 3 === 0 && isCurrentMonth ? mockPosts : [];

      days.push({ date, posts, isCurrentMonth, isToday });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  return (
    <div>
      {/* Header with Outlook Sync */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Content Calendar</h1>
            <p className="text-gray-600">Plan and schedule your social media content</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCollaboratorsModal(true)}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <Users className="h-5 w-5 mr-2" />
              Collaborators ({collaborators.length})
            </button>
            <button
              onClick={() => setShowSyncModal(true)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Bulk Sync
            </button>
            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="h-5 w-5 mr-2" />
              Schedule Post
            </button>
          </div>
        </div>

        {/* Client Selector */}
        <div className="flex items-center gap-4 mb-4">
          <label className="text-sm font-medium text-gray-700">Client:</label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>

        {/* Outlook Sync Status Bar */}
        <div className={`p-4 rounded-lg border ${outlookConnected ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {outlookConnected ? (
                <>
                  <Cloud className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-900">Outlook Calendar Connected</p>
                    <p className="text-sm text-green-700">
                      {selectedConnection?.email} • Last synced: {selectedConnection?.last_sync_at ? new Date(selectedConnection.last_sync_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <CloudOff className="h-6 w-6 text-yellow-600" />
                  <div>
                    <p className="font-semibold text-yellow-900">Outlook Calendar Not Connected</p>
                    <p className="text-sm text-yellow-700">Connect to automatically sync posts to Outlook Calendar</p>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              {outlookConnected ? (
                <>
                  <button
                    onClick={() => loadData()}
                    disabled={syncing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {syncing ? <RefreshCw className="h-5 w-5 animate-spin" /> : 'Refresh'}
                  </button>
                  <button
                    onClick={handleDisconnectOutlook}
                    className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={handleConnectOutlook}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Connect Outlook
                </button>
              )}
            </div>
          </div>

          {/* Sync Stats */}
          {syncStatus && (
            <div className="grid grid-cols-5 gap-4 mt-4 pt-4 border-t border-green-200">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-900">{syncStatus.total_posts || 0}</p>
                <p className="text-xs text-green-700">Total Posts</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{syncStatus.synced_posts || 0}</p>
                <p className="text-xs text-green-700">Synced</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{syncStatus.pending_posts || 0}</p>
                <p className="text-xs text-green-700">Pending</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{syncStatus.failed_posts || 0}</p>
                <p className="text-xs text-green-700">Failed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-900">{syncStatus.sync_percentage || 0}%</p>
                <p className="text-xs text-green-700">Sync Rate</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigateMonth('prev')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-semibold text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button onClick={() => navigateMonth('next')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => (
              <div
                key={index}
                className={`min-h-[120px] p-2 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-50 ${
                  !day.isCurrentMonth ? 'bg-gray-50 opacity-50' : ''
                } ${day.isToday ? 'bg-blue-50 border-blue-200' : ''}`}
                onClick={() => setSelectedDate(day.date)}
              >
                <div className={`text-sm font-medium mb-2 ${day.isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                  {day.date.getDate()}
                </div>

                <div className="space-y-1">
                  {day.posts.slice(0, 2).map((post: any) => (
                    <div key={post.id} className="p-1 bg-white rounded border border-gray-200 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 truncate flex-1">{post.title}</span>
                        {post.synced && <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />}
                        {!post.synced && outlookConnected && <AlertCircle className="h-3 w-3 text-yellow-600 flex-shrink-0" />}
                      </div>
                      <div className="text-gray-500 text-xs">{post.time}</div>
                    </div>
                  ))}
                  {day.posts.length > 2 && (
                    <div className="text-xs text-gray-500 text-center">+{day.posts.length - 2} more</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </h3>
            <button onClick={() => setSelectedDate(null)} className="text-gray-400 hover:text-gray-600">×</button>
          </div>

          <div className="space-y-4">
            {mockPosts.map((post) => (
              <div key={post.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-gray-900">{post.title}</h4>
                    {post.synced ? (
                      <span className="flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Synced to Outlook
                      </span>
                    ) : (
                      <span className="flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Not Synced
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">{post.client} • {post.time} • {post.platforms.join(', ')}</div>
                </div>

                <div className="flex items-center space-x-2">
                  {!post.synced && outlookConnected && (
                    <button
                      onClick={() => handleSyncPost(post.id)}
                      disabled={syncing}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {syncing ? 'Syncing...' : 'Sync to Outlook'}
                    </button>
                  )}
                  <button className="p-2 text-gray-400 hover:text-blue-600">
                    <Eye className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-blue-600">
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collaborators Modal */}
      {showCollaboratorsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-3xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Project Collaborators</h2>
              <button onClick={() => setShowCollaboratorsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Collaborators will be automatically added as attendees to Outlook calendar events for scheduled posts.
            </p>

            {/* Collaborators List */}
            <div className="space-y-3 mb-6">
              {collaborators.map((collab) => (
                <div key={collab.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">{collab.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{collab.name}</p>
                        <p className="text-sm text-gray-600">{collab.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{collab.role}</span>
                    {collab.receive_calendar_invites && (
                      <CheckCircle className="h-5 w-5 text-green-600" title="Receives calendar invites" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Collaborator Form */}
            <form onSubmit={handleAddCollaborator} className="border-t pt-4">
              <h3 className="font-semibold mb-3">Add New Collaborator</h3>
              <div className="grid grid-cols-2 gap-3">
                <input name="name" placeholder="Full Name" required className="px-4 py-2 border rounded-lg" />
                <input name="email" type="email" placeholder="Email" required className="px-4 py-2 border rounded-lg" />
                <input name="outlook_email" type="email" placeholder="Outlook Email (optional)" className="px-4 py-2 border rounded-lg" />
                <select name="role" required className="px-4 py-2 border rounded-lg">
                  <option value="">Select Role</option>
                  <option value="account_manager">Account Manager</option>
                  <option value="content_creator">Content Creator</option>
                  <option value="designer">Designer</option>
                  <option value="copywriter">Copywriter</option>
                  <option value="quality_reviewer">Quality Reviewer</option>
                </select>
              </div>
              <label className="flex items-center gap-2 mt-3">
                <input type="checkbox" name="receive_invites" defaultChecked className="rounded" />
                <span className="text-sm text-gray-700">Receive calendar invites</span>
              </label>
              <button type="submit" className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Add Collaborator
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Bulk Sync to Outlook</h2>
            <p className="text-gray-600 mb-6">
              This will sync all scheduled posts for the next 30 days to Outlook Calendar.
              Each post will be added as a calendar event with all collaborators as attendees.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSyncModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSync}
                disabled={syncing}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {syncing ? 'Syncing...' : 'Start Sync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
